import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { StateSelect, US_STATES } from "../../components/fields/StateSelect";
import { PlusCodeMap } from "../../components/fields/PlusCodeMap";
// @ts-expect-error no types
import { OpenLocationCode } from "open-location-code";

const olc = new OpenLocationCode();

export interface StationFormData {
  name: string;
  city: string;
  state: string;
  zip_code: string;
  county: string;
  address: string;
  contact_email: string;
  contact_number: string;
  rms_used: string;
  bodycam_vendor: string;
  seat_limit: string;
  subscription_tier: string;
  service_area_plus_code: string;
}

export type FormErrors = Partial<Record<keyof StationFormData, string>>;

export const EMPTY_FORM: StationFormData = {
  name: "",
  city: "",
  state: "",
  zip_code: "",
  county: "",
  address: "",
  contact_email: "",
  contact_number: "",
  rms_used: "",
  bodycam_vendor: "",
  seat_limit: "",
  subscription_tier: "",
  service_area_plus_code: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s\-(). ]{7,20}$/;
const PLUS_CODE_RE = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,7}$/i;

const PLUS_CODE_INITIAL_DELAY_MS = 3000;
const PLUS_CODE_MAX_DELAY_MS = 60000;
// Nominatim usage policy requires identifying the application via Referer or
// User-Agent. The browser sets Referer automatically; we also pass `email` as
// the recommended contact identifier.
// TODO: confirm the right inbox before deploying.
const NOMINATIM_CONTACT_EMAIL = "support@kardanalytics.com";

// Policy: "Results must be cached on client side. Repeated identical queries
// may trigger blocking." A null entry means the address was geocoded but had
// no results — still worth caching to avoid re-asking.
const plusCodeCache = new Map<string, string | null>();

export function validateForm(data: StationFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.name.trim()) errors.name = "Required";
  if (!data.address.trim()) errors.address = "Required";
  if (!data.city.trim()) errors.city = "Required";
  if (!data.county.trim()) errors.county = "Required";
  if (!data.zip_code.trim()) errors.zip_code = "Required";

  if (!data.state.trim()) {
    errors.state = "Required";
  } else if (!US_STATES.includes(data.state.toUpperCase())) {
    errors.state = "Must be a valid US state abbreviation (e.g. CA, TX, DC)";
  }

  if (data.contact_email.trim() && !EMAIL_RE.test(data.contact_email)) {
    errors.contact_email = "Must be a valid email address";
  }

  if (data.contact_number.trim() && !PHONE_RE.test(data.contact_number)) {
    errors.contact_number = "Must be a valid phone number";
  }

  if (!data.bodycam_vendor) errors.bodycam_vendor = "Required";
  if (!data.subscription_tier) errors.subscription_tier = "Required";

  const seats = parseInt(data.seat_limit, 10);
  if (!data.seat_limit.trim()) {
    errors.seat_limit = "Required";
  } else if (isNaN(seats) || seats <= 0) {
    errors.seat_limit = "Must be a positive number";
  }

  if (!data.service_area_plus_code.trim()) {
    errors.service_area_plus_code =
      "Required — automatically calculated from address or enter manually";
  } else if (!PLUS_CODE_RE.test(data.service_area_plus_code)) {
    errors.service_area_plus_code =
      "Must be a valid Plus Code (e.g. 8FVC9G8F+6W)";
  }

  return errors;
}

export interface StationFormProps {
  formData: StationFormData;
  formErrors: FormErrors;
  handleFieldChange: (field: keyof StationFormData, value: string) => void;
  disabled: boolean;
  textFieldSx?: any;
}

export const StationForm: React.FC<StationFormProps> = ({
  formData,
  formErrors,
  handleFieldChange,
  disabled,
  textFieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } },
}) => {
  const [isCalculatingPlusCode, setIsCalculatingPlusCode] = useState(false);
  const [plusCodeError, setPlusCodeError] = useState<string | null>(null);
  const backoffDelayRef = useRef(PLUS_CODE_INITIAL_DELAY_MS);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const { address, city, state, zip_code } = formData;
    if (
      disabled ||
      !address.trim() ||
      !city.trim() ||
      !state.trim() ||
      !zip_code.trim()
    ) {
      handleFieldChange("service_area_plus_code", "");
      setPlusCodeError(null);
      setIsCalculatingPlusCode(false);
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    const query = [address, city, state, zip_code].filter(Boolean).join(", ");

    if (plusCodeCache.has(query)) {
      const cached = plusCodeCache.get(query);
      if (cached) {
        handleFieldChange("service_area_plus_code", cached);
        setPlusCodeError(null);
      } else {
        handleFieldChange("service_area_plus_code", "");
        setPlusCodeError(
          "Could not geocode address. Please verify the address fields.",
        );
      }
      setIsCalculatingPlusCode(false);
      return;
    }

    const fetchPlusCode = async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsCalculatingPlusCode(true);
      setPlusCodeError(null);
      try {
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("q", query);
        url.searchParams.set("format", "json");
        url.searchParams.set("limit", "1");
        url.searchParams.set("email", NOMINATIM_CONTACT_EMAIL);
        const resp = await fetch(url.toString(), {
          headers: { "Accept-Language": "en" },
          signal: controller.signal,
          referrerPolicy: "strict-origin-when-cross-origin",
        });
        if (resp.status === 429) {
          const nextDelay = Math.min(
            backoffDelayRef.current * 2,
            PLUS_CODE_MAX_DELAY_MS,
          );
          backoffDelayRef.current = nextDelay;
          setPlusCodeError(
            `Geocoding service is rate limited. Retrying in ${Math.round(nextDelay / 1000)}s...`,
          );
          timeoutRef.current = setTimeout(fetchPlusCode, nextDelay);
          return;
        }
        if (!resp.ok) {
          handleFieldChange("service_area_plus_code", "");
          setPlusCodeError("Failed to calculate Plus Code. Please try again.");
          return;
        }
        const results = await resp.json();
        if (Array.isArray(results) && results.length > 0) {
          const { lat, lon } = results[0];
          const plusCode: string = olc.encode(
            parseFloat(lat),
            parseFloat(lon),
            10,
          );
          plusCodeCache.set(query, plusCode);
          handleFieldChange("service_area_plus_code", plusCode);
          backoffDelayRef.current = PLUS_CODE_INITIAL_DELAY_MS;
        } else {
          plusCodeCache.set(query, null);
          handleFieldChange("service_area_plus_code", "");
          setPlusCodeError(
            "Could not geocode address. Please verify the address fields.",
          );
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        handleFieldChange("service_area_plus_code", "");
        setPlusCodeError("Failed to calculate Plus Code. Please try again.");
      } finally {
        if (!controller.signal.aborted) {
          setIsCalculatingPlusCode(false);
        }
      }
    };

    timeoutRef.current = setTimeout(fetchPlusCode, backoffDelayRef.current);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [
    formData.address,
    formData.city,
    formData.state,
    formData.zip_code,
    disabled,
  ]);

  return (
    <Grid container spacing={2.5} sx={{ pt: 1 }}>
      {/* Station Name */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Station Name"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          disabled={disabled}
          error={!!formErrors.name}
          helperText={formErrors.name}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* Address */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Address"
          required
          fullWidth
          variant="outlined"
          value={formData.address}
          onChange={(e) => handleFieldChange("address", e.target.value)}
          disabled={disabled}
          error={!!formErrors.address}
          helperText={formErrors.address}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* City | State */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="City"
          required
          fullWidth
          variant="outlined"
          value={formData.city}
          onChange={(e) => handleFieldChange("city", e.target.value)}
          disabled={disabled}
          error={!!formErrors.city}
          helperText={formErrors.city}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <StateSelect
          required
          value={formData.state}
          onChange={(newValue) => handleFieldChange("state", newValue)}
          disabled={disabled}
          error={!!formErrors.state}
          helperText={formErrors.state}
          sx={textFieldSx}
        />
      </Grid>

      {/* Zip Code | County */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Zip Code"
          required
          fullWidth
          variant="outlined"
          value={formData.zip_code}
          onChange={(e) => handleFieldChange("zip_code", e.target.value)}
          disabled={disabled}
          error={!!formErrors.zip_code}
          helperText={formErrors.zip_code}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="County"
          required
          fullWidth
          variant="outlined"
          value={formData.county}
          onChange={(e) => handleFieldChange("county", e.target.value)}
          disabled={disabled}
          error={!!formErrors.county}
          helperText={formErrors.county}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* Contact Email | Contact Number */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Contact Email"
          fullWidth
          variant="outlined"
          value={formData.contact_email}
          onChange={(e) => handleFieldChange("contact_email", e.target.value)}
          disabled={disabled}
          error={!!formErrors.contact_email}
          helperText={formErrors.contact_email}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Contact Number (optional)"
          fullWidth
          variant="outlined"
          value={formData.contact_number}
          onChange={(e) => handleFieldChange("contact_number", e.target.value)}
          disabled={disabled}
          error={!!formErrors.contact_number}
          helperText={formErrors.contact_number}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* RMS Used | Bodycam Vendor */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth error={!!formErrors.rms_used}>
          <InputLabel>RMS Used</InputLabel>
          <Select
            label="RMS Used"
            value={formData.rms_used}
            onChange={(e) => handleFieldChange("rms_used", e.target.value)}
            disabled={disabled}
            sx={{
              borderRadius:
                textFieldSx?.["& .MuiOutlinedInput-root"]?.borderRadius ?? 0,
            }}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="EF">Eforce</MenuItem>
            <MenuItem value="CS">Central Square</MenuItem>
            <MenuItem value="SP">Spillman</MenuItem>
            <MenuItem value="SR">SunRidge</MenuItem>
            <MenuItem value="TN">TrueNorth</MenuItem>
          </Select>
          {formErrors.rms_used && (
            <FormHelperText>{formErrors.rms_used}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth error={!!formErrors.bodycam_vendor}>
          <InputLabel>Bodycam Vendor</InputLabel>
          <Select
            label="Bodycam Vendor"
            value={formData.bodycam_vendor}
            onChange={(e) =>
              handleFieldChange("bodycam_vendor", e.target.value)
            }
            disabled={disabled}
            sx={{
              borderRadius:
                textFieldSx?.["& .MuiOutlinedInput-root"]?.borderRadius ?? 0,
            }}
          >
            <MenuItem value="KARDA">None</MenuItem>
            <MenuItem value="AXON">AXON</MenuItem>
            <MenuItem value="LENSLOCK">LENSLOCK</MenuItem>
            <MenuItem value="WATCHGUARD">WATCHGUARD</MenuItem>
          </Select>
          {formErrors.bodycam_vendor && (
            <FormHelperText>{formErrors.bodycam_vendor}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Seat Limit | Subscription Tier */}
      <Grid size={{ xs: 12, sm: 2 }}>
        <TextField
          label="Seat Limit"
          fullWidth
          variant="outlined"
          type="number"
          value={formData.seat_limit}
          onChange={(e) => handleFieldChange("seat_limit", e.target.value)}
          disabled={disabled}
          error={!!formErrors.seat_limit}
          helperText={formErrors.seat_limit}
          inputProps={{ min: 1 }}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth error={!!formErrors.subscription_tier}>
          <InputLabel>Subscription</InputLabel>
          <Select
            label="Subscription"
            value={formData.subscription_tier}
            onChange={(e) =>
              handleFieldChange("subscription_tier", e.target.value)
            }
            disabled={disabled}
            sx={{
              borderRadius:
                textFieldSx?.["& .MuiOutlinedInput-root"]?.borderRadius ?? 0,
            }}
          >
            <MenuItem value="TRIAL">TRIAL</MenuItem>
            <MenuItem value="PRO">PRO</MenuItem>
          </Select>
          {formErrors.subscription_tier && (
            <FormHelperText>{formErrors.subscription_tier}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Service Area Plus Code */}
      <Grid size={{ xs: 6 }}>
        <TextField
          label="Service Area Plus Code"
          fullWidth
          variant="outlined"
          value={formData.service_area_plus_code}
          onChange={(e) =>
            handleFieldChange(
              "service_area_plus_code",
              e.target.value.toUpperCase(),
            )
          }
          disabled={disabled} // or make readOnly
          error={!!formErrors.service_area_plus_code || !!plusCodeError}
          helperText={
            formErrors.service_area_plus_code ||
            plusCodeError ||
            "Automatically calculated from address"
          }
          placeholder="e.g. 8FVC9G8F+6W"
          sx={textFieldSx}
          InputProps={{
            readOnly: true,
            endAdornment: isCalculatingPlusCode ? (
              <CircularProgress color="inherit" size={20} />
            ) : null,
          }}
        />
      </Grid>

      {/* Service Area Map Preview */}
      {formData.service_area_plus_code && (
        <Grid size={{ xs: 12 }}>
          <PlusCodeMap plusCode={formData.service_area_plus_code} />
        </Grid>
      )}
    </Grid>
  );
};
